// Filename: UserCard.js
import React from "react";
import {
  Box,
  Paper,
  Avatar,
  Typography,
  Stack,
  Chip,
  Divider,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { green, red, grey } from "@mui/material/colors";
import { tokens } from "../theme";
import { Diversity3, Flag, FlagCircle, Warning } from "@mui/icons-material";

const UserCard = ({
  user,
  showDetails = false,
  handleFlag,
  loadingUserId,
  single = false,
}) => {
  const isLoading = loadingUserId === user.id;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const statusColor =
    user.status === "active"
      ? green[500]
      : user.status === "pending"
      ? grey[600]
      : red[500];
  const verifiedColor = user.verified ? green[700] : red[700];

  const getRoleDetails = () => {
    if (user.role === "TENANT") {
      return user.unit ? (
        <div className="notranslate">
          <Typography variant="body2">
            🏢 {user.unit.buildingName}, Floor {user.unit.floor}, Flat{" "}
            {user.unit.flatNumber}
          </Typography>
          <Typography variant="body2">
            Compound: {user.unit.compoundName}
          </Typography>
        </div>
      ) : (
        <Chip label="No Rented Unit" color="warning" size="small" />
      );
    } else if (user.role === "LANDLORD") {
      return user.properties?.length ? (
        <div className="notranslate">
          {user.properties.map((p, i) => (
            <Typography key={i} variant="body2">
              🏠 {p.buildingName}, Floor {p.floor}, Flat {p.flatNumber}{" "}
              (Compound: {p.compoundName})
            </Typography>
          ))}
        </div>
      ) : (
        <Chip label="No Properties" color="warning" size="small" />
      );
    } else if (user.role === "SERVICE_PROVIDER") {
      return (
        <div>
          <Typography variant="body2">🛠 Service: {user.serviceName}</Typography>
          <Typography variant="body2" className="notranslate">
            Compound: {user.compoundName}
          </Typography>
        </div>
      );
    }
    return null;
  };

  return (
    <Paper elevation={2} sx={{ p: 2, position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {!single && user.isFlagged && (
          <Chip icon={<Flag />} label="Flagged" color="error" size="small" />
        )}

        <Chip
          icon={<Diversity3 />}
          label={user.compoundName}
          color="warning"
          size="small"
          className="notranslate"
        />
      </Box>
      {single ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box className="notranslate">
            <Avatar
              src={user.avatar_url}
              alt={user.fullName}
              sx={{ width: 56, height: 56 }}
            />
          </Box>
          <Box flex={1} className="notranslate">
            <Typography variant="subtitle1" className="notranslate">
              {user.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user.username} | {user.email}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              <Typography variant="body2" color="text.secondary" dir="ltr">
                {user.phone}
              </Typography>
            </Box>
          </Box>
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={user.avatar_url}
            alt={user.fullName}
            sx={{ width: 56, height: 56 }}
            className="notranslate"
          />
          <Box flex={1} >
            <Typography variant="subtitle1" className="notranslate">{user.fullName}</Typography>
            <Typography variant="body2" color="text.secondary" className="notranslate">
              @{user.username} | {user.email}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5} className="notranslate">
              <Typography variant="body2" color="text.secondary" dir="ltr">
                {user.phone}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} mt={1}>
              <Chip
                label={user.status}
                sx={{ backgroundColor: statusColor, color: "white" }}
                size="small"
              />
              <Chip
                label={user.verified ? "Verified" : "Unverified"}
                sx={{ backgroundColor: verifiedColor, color: "white" }}
                size="small"
              />
              <Chip label={user.role.toUpperCase()} color="info" size="small" />
              {user.status === "pending" && (
                <Chip label="New User" color="success" size="small" />
              )}
            </Stack>
            {showDetails && (
              <Box mt={1}>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Box>{getRoleDetails()}</Box>
                  <Box>
                    <IconButton
                      onClick={() => handleFlag(user.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="warning" />
                      ) : (
                        <FlagCircle
                          sx={{
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.2) rotate(15deg)",
                            },
                          }}
                        />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default UserCard;
